// ═══════════════════════════════════════════════════════════════════
// venusModel3D.jsx — VENUS, SCULPTED   (caP'n, 2026-09-16; REBUILT 2026-09-22)
// ═══════════════════════════════════════════════════════════════════
// Card Packs — the zombie card-shop girl, at her stall. caP'n, 2026-09-22:
// "stands still maybe waves her hand to entice the customer etc but
// nothing crazy, she's just at her stall".
//
// WHAT IT IS. `public/models/venus.glb` — REBUILT IN BLENDER 2026-09-22 on
// V'ael's pipeline (master gamemaster/models/sculpt/glb/venus.glb, builder
// gamemaster/models/sculpt/builders/venus_blender/), off caP'n's full-body
// art (Venus_plaza_1) and the Plaza close-up. The 2026-09-16 SDF body is
// kept beside it as venus_v1_sdf.glb. Metres, feet at y=0, facing +z,
// crown at MODEL_TOP. 17 named meshes on ONE 21-bone skeleton (V'ael's
// layout + her 3-bone tail).
//   · The SKIN carries VERTEX COLOURS — the stitched patchwork — times its
//     face texture; the stitches are their own mesh (no outline). The ears
//     carry vertex colours too (stone; grey with moss).
//   · ⚑ HER REST POSE IS THE ART'S POSE: elbows down, forearms up, a fan of
//     cards in each hand (2 right, 3 left) bound to the hand bones. Every
//     rotation below is a small delta from that hold.
// Idle: a slow sway, the sly head tilt, looking round the Plaza, the tail
// swishing — and every few seconds her LEFT hand gives the cards a little
// "over here!" wave. Walk (ctl.speed) keeps the cards up.
//
// Same contract and safety net as the other folk sculpts:
// <VenusSculpted ctl h Fallback> for r3f scenes; the painted VenusBody
// stands in while the file loads or if it fails, and failSafe() in
// plazaFolk3D.jsx swaps it in for good if the frame rate can't hold.
// PERF LAW: nothing here sets React state from a frame.
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

export const VENUS_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/venus.glb`;
const MODEL_TOP = 1.636;     // measured at export (Blender build, 2026-09-22): the bun's top
// THE INK HULL'S WIDTH, PER PART — mirrored in the builder's ven_look.py (INK_BY_PART).
// Thin parts get a thinner line (a full-width hull is wider than the part); 0 = no line.
const INK_W = 0.0032;
const INK_BY_PART = [[/stitches/, 0], [/laces/, 0.0012], [/cord/, 0.0014], [/cards/, 0.0014], [/hoop/, 0.0010],
  [/flowers/, 0.0014], [/cross/, 0.0016], [/tuft/, 0.0024]];
const inkWidth = (name) => (INK_BY_PART.find(([re]) => re.test(name)) || [null, INK_W])[1];
const SELF_LIGHT = 0.4;

let RAMP = null;
const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([172, 172, 172, 255, 228, 228, 228, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter;
  RAMP.needsUpdate = true;
  return RAMP;
};
const INK = new Map();
const inkMat = (w) => {
  let m = INK.get(w);
  if (m) return m;
  m = new THREE.MeshBasicMaterial({ color: 0x161114, side: THREE.BackSide });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace("#include <begin_vertex>",
      "#include <begin_vertex>\n  transformed += normalize( normal ) * " + w.toFixed(5) + ";");
  };
  m.customProgramCacheKey = () => "venusInk" + w;     // two widths share a signature without this
  INK.set(w, m);
  return m;
};

function prepare(scene) {
  const root = cloneSkinned(scene);
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  meshes.forEach((o) => {
    const m = o.material;
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    const vc = !!o.geometry.attributes.color;
    o.material = new THREE.MeshToonMaterial({
      color: m.color, map: m.map, gradientMap: toonRamp(), side: m.side, vertexColors: vc,
      emissive: m.color.clone().multiplyScalar(SELF_LIGHT), emissiveMap: m.map || null,
    });
    // the patchwork lives in the vertex colours: the self-light must be scaled by them
    // too, or it washes every patch toward the same grey
    if (vc) {
      o.material.onBeforeCompile = (sh) => {
        sh.fragmentShader = sh.fragmentShader.replace("vec3 totalEmissiveRadiance = emissive;",
          "vec3 totalEmissiveRadiance = emissive * vColor.rgb;");
      };
      o.material.customProgramCacheKey = () => "venusVC";
    }
    o.frustumCulled = false;
    const w = inkWidth(o.name);
    if (w > 0) {
      const ink = o.isSkinnedMesh ? new THREE.SkinnedMesh(o.geometry, inkMat(w)) : new THREE.Mesh(o.geometry, inkMat(w));
      if (o.isSkinnedMesh) ink.bind(o.skeleton, o.bindMatrix);
      ink.name = o.name + "_ink"; ink.frustumCulled = false;
      o.parent.add(ink);
    }
  });
  const B = {};
  root.traverse((o) => { if (o.isBone) B[o.name] = o; });
  if (B.hips) B.hips.userData.y0 = B.hips.position.y;
  return { root, B };
}

// the "over here!" wave: a 2.2 s window every 7 s, eased in and out
const WAVE_EVERY = 7.0, WAVE_LEN = 2.2;
export const waveEnv = (t) => {
  const p = (t % WAVE_EVERY) / WAVE_LEN;
  return p < 1 ? Math.sin(Math.PI * p) ** 2 : 0;
};

// VENUS — at her stall. Rest = the art's hold (cards up); all of this is deltas.
// ⚠ Mirrored in the builder (ven_export.venus_pose) for the stretch test: change both.
export function venusPose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const still = 1 - amt, sw = Math.sin(ph);
  const wv = waveEnv(t) * still, wf = Math.sin(t * 8.5);
  for (const [k, s, off] of [["L", 1, 0], ["R", -1, Math.PI]]) {
    const lp = ph + off, fwd = Math.sin(lp), swing = Math.max(0, Math.cos(lp));
    set("thigh" + k, (-fwd * 0.36 - swing * 0.12) * amt, 0, 0);
    set("shin" + k, swing * 0.70 * amt, 0, 0);
    set("foot" + k, (-swing * 0.30 + Math.max(0, -fwd) * 0.18) * amt, 0, 0);
    // both fans bob a little ("buy a pack!"), a touch out of step
    const bob = Math.sin(t * 1.4 + (s > 0 ? 0 : 1.3)) * 0.03 + Math.abs(Math.cos(ph)) * 0.04 * amt;
    const w = s > 0 ? wv : 0;                     // only her LEFT hand waves
    set("upperarm" + k, bob * 0.4, 0, 0);
    set("forearm" + k, -bob, 0, w * wf * 0.14);   // the wrist flick below does most of it: the folded elbow tears past ~0.2
    set("hand" + k, bob * 0.5 + w * Math.sin(t * 8.5 - 0.5) * 0.18, 0, w * Math.sin(t * 8.5 - 0.9) * 0.42);
  }
  set("hips", 0, sw * 0.06 * amt, Math.sin(t * 0.7) * 0.02 * still + sw * 0.03 * amt);
  if (B.hips) B.hips.position.y = B.hips.userData.y0 + (Math.abs(Math.cos(ph)) - 0.6) * 0.016 * amt;
  set("spine", 0, -sw * 0.04 * amt, -Math.sin(t * 0.7) * 0.015 * still);
  set("chest", Math.sin(t * 1.5) * 0.012, -sw * 0.03 * amt, wv * 0.03);
  set("neck", 0, 0, 0);
  // the sly tilt; she looks round the Plaza, and toward her waving hand when she waves
  set("head", -0.03 + Math.sin(t * 0.8) * 0.015,
    (Math.sin(t * 0.33) * 0.22 + wv * 0.12) * (1 - 0.6 * amt),
    0.07 + Math.sin(t * 0.45) * 0.03 + wv * 0.05);
  set("tail1", Math.sin(t * 0.9) * 0.04, Math.sin(t * 1.1) * 0.12, 0);
  set("tail2", 0, Math.sin(t * 1.1 - 0.7) * 0.18, Math.sin(t * 0.8) * 0.05);
  set("tail3", Math.sin(t * 1.3 - 1.2) * 0.08, Math.sin(t * 1.1 - 1.4) * 0.26, 0);
}

export function VenusModel({ ctl, h = 1.6 }) {
  const gltf = useLoader(GLTFLoader, VENUS_GLB_URL);
  const { root, B } = useMemo(() => prepare(gltf.scene), [gltf]);
  const g = useRef(); const yaw = useRef(0); const ph = useRef(0); const amt = useRef(0);
  useFrame(({ clock }, dtRaw) => {
    const o = g.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 3.1;
    const f = (ctl && ctl.facing) || { x: 0, y: 1 };
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 9);
    o.rotation.y = yaw.current;
    const speed = (ctl && ctl.speed) || 0;
    amt.current += ((speed ? 1 : 0) - amt.current) * Math.min(1, dt * 6);
    ph.current += (speed ? 1 : amt.current) * dt * 7;
    venusPose(B, t, ph.current, amt.current);
  });
  return (
    <group ref={g}>
      <primitive object={root} scale={h / MODEL_TOP} />
    </group>
  );
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[venus] venus.glb did not load — the painted body stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

export function VenusSculpted({ ctl, h, Fallback }) {
  const fb = Fallback ? <Fallback ctl={ctl} h={h} /> : null;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <VenusModel ctl={ctl} h={h} />
      </Suspense>
    </GlbBoundary>
  );
}

export default VenusModel;
